import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';

const Dashboard = ({
  permissionSetAssignments = [],
  fetchPermissionSetAssignments,
}) => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);

  // Store counts from paginated API responses
  const [counts, setCounts] = useState({
    users: 0,
    groups: 0,
    permissionSets: 0,
    awsAccounts: 0,
    assignments: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (dataInitialized || isLoading) {
        return;
      }

      setIsLoading(true);

      try {
        // Fetch counts from paginated APIs (page 1, pageSize 1 to just get totals)
        const [usersRes, groupsRes, permissionSetsRes, accountsRes] = await Promise.all([
          apiService.getAllUsers({ page: 1, pageSize: 1 }),
          apiService.getAllGroups({ page: 1, pageSize: 1 }),
          apiService.getAllPermissionSets({ page: 1, pageSize: 1 }),
          apiService.getAwsAccountsFromDB({ page: 1, pageSize: 1 }),
        ]);

        // Also fetch assignments
        if (fetchPermissionSetAssignments) {
          await fetchPermissionSetAssignments();
        }

        setCounts(prev => ({
          ...prev,
          users: usersRes?.data?.total || 0,
          groups: groupsRes?.data?.total || 0,
          permissionSets: permissionSetsRes?.data?.total || 0,
          awsAccounts: accountsRes?.data?.total || 0,
        }));

        setDataInitialized(true);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setDataInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update assignments count when prop changes
  useEffect(() => {
    if (permissionSetAssignments) {
      setCounts(prev => ({
        ...prev,
        assignments: permissionSetAssignments.length,
      }));
    }
  }, [permissionSetAssignments]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}
      >
        <CircularProgress size={48} sx={{ color: '#4698D3' }} />
        <Typography
          sx={{
            color: '#666666',
            fontFamily: 'Metropolis, sans-serif',
            fontWeight: 500,
            fontSize: '1rem',
          }}
        >
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  const dashboardCards = [
    {
      title: 'Users',
      count: counts.users,
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      color: '#4698D3',
      path: '/users',
      description: 'Manage user accounts'
    },
    {
      title: 'Groups',
      count: counts.groups,
      icon: <GroupIcon sx={{ fontSize: 28 }} />,
      color: '#D64794',
      path: '/groups',
      description: 'Organize users into groups'
    },
    {
      title: 'Permission Sets',
      count: counts.permissionSets,
      icon: <SecurityIcon sx={{ fontSize: 28 }} />,
      color: '#27AE60',
      path: '/permission-sets',
      description: 'Define access permissions'
    },
    {
      title: 'AWS Accounts',
      count: counts.awsAccounts,
      icon: <CloudIcon sx={{ fontSize: 28 }} />,
      color: '#F39C12',
      path: '/accounts',
      description: 'Onboarded AWS accounts'
    },
    {
      title: 'Assignments',
      count: counts.assignments,
      icon: <AssignmentIcon sx={{ fontSize: 28 }} />,
      color: '#9B59B6',
      path: '/permission-set-assignments',
      description: 'Permission assignments'
    }
  ];

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        padding: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E5E5',
            p: 3,
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#4698D3',
                width: 48,
                height: 48,
              }}
            >
              <DashboardIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Dashboard
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                Overview of your identity management resources
              </Typography>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* Dashboard Cards */}
      <motion.div variants={sectionVariants}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {dashboardCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.15 + index * 0.06, ease: [0.22, 1, 0.36, 1] }}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    borderColor: card.color,
                    boxShadow: `0 4px 12px ${card.color}20`,
                  },
                }}
                onClick={() => navigate(card.path)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '10px',
                        backgroundColor: `${card.color}12`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: card.color,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: 'Metropolis, sans-serif',
                        fontWeight: 700,
                        fontSize: '2.5rem',
                        color: card.color,
                        lineHeight: 1,
                      }}
                    >
                      {card.count}
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#253E66',
                      mb: 0.5,
                    }}
                  >
                    {card.title}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: 'Metropolis, sans-serif',
                      fontSize: '0.8rem',
                      color: '#999999',
                      fontWeight: 500,
                    }}
                  >
                    {card.description}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      </motion.div>
    </motion.div>
  );
};

export default Dashboard;
